<script>
	import analyticsUtilities from 'utilities/analyticsUtilities';
	import breakpoints from 'utilities/breakpoints';
	import optimizedResize from 'utilities/optimizedResize';
	import Carousel from 'components/carousel.vue';
	import CarouselSlide from 'components/carousel-slide.vue';
	import OutboundLink from 'components/outbound-link.vue';
	import DoubleQuoteIcon from 'components/svg/double-quote-icon.vue';

	export default {
		components: {
			carousel: Carousel,
			'carousel-slide': CarouselSlide,
			'outbound-link': OutboundLink,
			'double-quote-icon': DoubleQuoteIcon
		},
		props: {
			panels: {
				type: Array,
				required: true
			},
			headerText: {
				type: String
			},
			name: {
				type: String
			}
		},
		data() {
			return {
				panelsPerSlide: 2,
				currentSlide: 0,
				setSlideFocus: false
			};
		},
		computed: {
			headerId() {
				return `testimonial-header-${this.name}`;
			},
			totalPages() {
				return Math.ceil(this.panels.length / this.panelsPerSlide);
			},
			slides() {
				const panels = this.panels || [];
				const perSlide = this.panelsPerSlide;
				const slides = new Array(this.totalPages).fill([]).map((page, index) => panels.slice(index * perSlide, (index + 1) * perSlide));

				return slides;
			}
		},
		methods: {
			getPanelsPerSlide() {
				let panelsPerSlide = 2;

				if (breakpoints.isSmallScreen()) {
					panelsPerSlide = 1;
				}

				this.panelsPerSlide = panelsPerSlide;
			},
			onSlideChange(slide, setSlideFocus) {
				this.currentSlide = slide;
				this.setSlideFocus = setSlideFocus;

				analyticsUtilities.fireEvent(analyticsUtilities.events.HOMEPAGE_CLICK, {
					loc: 'testimonial-carousel',
					slide: slide + 1
				});
			}
		},
		mounted() {
			this.getPanelsPerSlide();

			optimizedResize.add(this.getPanelsPerSlide);
		},
		beforeDestroy() {
			optimizedResize.remove(this.getPanelsPerSlide);
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'mixins/media';

	.testimonials-panel {
		padding: 3rem 0;
		background-color: $c-blue-accent-1;
		color: $c-white;

		h2 {
			margin-top: 0;
			text-align: center;
		}

		.carousel {
			.arrow {
				font-size: 2rem;
				color: $c-white;

				&:hover {
					color: $c-babyblue;
				}

				@include media(med) {
					font-size: 3rem;
				}
			}

			.slide-container {
				min-height: 30rem;
				align-items: center;

				@include media(med, max) {
					padding: 0;
				}
			}

			.carousel-slide {
				display: grid;
				grid-template-columns: 1fr;
				gap: 1rem;

				@include media(med) {
					grid-template-columns: repeat(2, 1fr);
					gap: 2rem;
				}

				@include media(lg) {
					gap: 4rem;
				}
			}

			.carousel-pagination-control {
				&,
				&.active {
					color: $c-white;
				}

				&:hover {
					background-color: rgba($c-white, 0.2);
				}

				&:not(.active) .pagination-dot {
					background-color: transparent;
				}
			}

			.pagination-dot {
				height: 0.75rem;
				width: 0.75rem;
			}
		}

		.testimonial {
			display: flex;
			flex-direction: column;
			min-height: 14.5rem;
			padding: 1.5rem;
			border-radius: 2rem;
			background-color: $c-white;
			color: $c-slate-muted;

			@include media(sm, max) {
				padding: 1rem;
			}

			@include media(med) {
				min-height: 17.5rem;
				max-width: 25rem;

				&:first-child {
					justify-self: flex-end;
				}

				&:last-child {
					justify-self: flex-start;
				}
			}
		}

		.double-quote-icon {
			height: 1em;
			width: 1em;
			display: inline;

			@include media(med) {
				display: block;
				height: 1.75rem;
				width: 1.75rem;
				margin-bottom: 1rem;
			}

			@include media(lg) {
				height: 2.5rem;
				width: 2.5rem;
			}
		}

		.testimonial-text {
			flex-grow: 1;

			@include media(sm, max) {
				font-size: 0.9rem;
			}

			@include media(med) {
				line-height: 1.5;
			}

			@include media(lg) {
				line-height: 1.75;
			}
		}

		.testimonial-meta {
			margin-top: 0.5rem;
			padding-top: 0.5rem;
			border-top: 1px solid $c-grey;
			display: flex;
			align-items: center;

			@include media(med) {
				margin-top: 1rem;
				padding-top: 1rem;
			}

			@include media(lg) {
				margin-top: 2rem;
			}
		}

		.testimonial-image {
			flex: 0 0 3rem;
			width: 3rem;
			height: 3rem;
			object-fit: cover;
			border-radius: 50%;

			@include media(med) {
				flex-basis: 5rem;
				width: 5rem;
				height: 5rem;
			}

			@include media(lg) {
				flex-basis: 8rem;
				width: 8rem;
				height: 8rem;
			}
		}

		.testimonial-meta-content {
			flex: 1 1;
			margin-left: 0.5rem;
			display: flex;
			flex-direction: column;
			justify-content: center;

			@include media(med, max) {
				font-size: 0.9rem;
			}

			@include media(med) {
				margin-left: 1rem;
			}
		}

		.testimonial-name {
			font-weight: 500;

			@include media(lg) {
				font-size: 1.25rem;
			}
		}

		.testimonial-title {
			text-transform: uppercase;

			@include media(lg) {
				font-size: 1.125rem;
			}
		}
	}
</style>
<template>
	<section class="testimonials-panel spotlight-bg">
		<div class="wrapper">
			<h2 :id="headerId">{{ headerText }}</h2>
			<carousel
				class="testimonials-carousel"
				show-arrows
				show-pagination
				:total-slides="slides.length"
				:label-id="headerId"
				@slide-change="onSlideChange"
				ref="carousel">
				<carousel-slide
					v-for="(slide, index) in slides"
					:key="index"
					class="testimonial-wrapper"
					:position="index"
					:total-slides="slides.length"
					:current-slide="currentSlide"
					:set-focus="setSlideFocus"
					animation="slide">
					<div
						class="testimonial"
						v-for="testimonial in slide"
						:key="testimonial.content_text">
						<div class="testimonial-text">
							<double-quote-icon></double-quote-icon>
							{{ testimonial.content_text }}
							<outbound-link
								v-if="testimonial.actions && testimonial.actions[0]"
								:href="testimonial.actions[0].url"
								:data-outbound-link="testimonial.actions[0].url || 'HOMEPAGE_TESTIMONIAL'"
								>{{ testimonial.actions[0].text }}</outbound-link
							>
						</div>
						<div class="testimonial-meta">
							<img
								v-if="testimonial.image"
								class="testimonial-image"
								loading="lazy"
								:src="testimonial.image.url"
								:alt="testimonial.image.alt_text" />
							<div
								v-if="testimonial.metadata && testimonial.metadata.review"
								class="testimonial-meta-content">
								<div class="testimonial-name">{{ testimonial.metadata.review.name }}</div>
								<div class="testimonial-title">{{ (testimonial.metadata.review.titles || []).join(', ') }}</div>
							</div>
						</div>
					</div>
				</carousel-slide>
			</carousel>
		</div>
	</section>
</template>
